import i18n from 'config/i18n';
import { isNil } from 'ramda';
import * as yup from 'yup';

import { getLocale } from 'utils/date';

const t = i18n.getFixedT(getLocale());

const minNumberSchema = (message: string) =>
  yup
    .number()
    .min(0, t('validation.greaterThanOrEqual', { field: 0 }))
    .required(t('validationFieldRequired', { name: message }))
    .typeError(t('validation.invalid', { name: message }));

const detailsSchema = yup.object({
  details: yup.lazy((arr) => {
    if (isNil(arr)) {
      return yup.array();
    }
    return yup
      .array()
      .of(
        yup.object().shape({
          flags: yup.array().default([]),
          id: yup.number(),
          description: yup.string(),
          method: yup.string().required(
            t('validationFieldRequired', {
              name: t('surchargesMaintenance.form.method')
            })
          ),
          surcharge1Duty: minNumberSchema(
            t('surchargesMaintenance.form.surcharge1Duty')
          ),
          surcharge1Nom: minNumberSchema(
            t('surchargesMaintenance.form.surcharge1Nom')
          ),
          surcharge1Perc: minNumberSchema(
            t('surchargesMaintenance.form.surcharge1Perc')
          )
            .max(
              100,
              t('validation.lessThan', {
                field: t('surchargesMaintenance.form.surcharge1Perc')
              })
            )
            .default(0),
          surcharge3Duty: minNumberSchema(
            t('surchargesMaintenance.form.surcharge3Duty')
          ),
          surcharge3Nom: minNumberSchema(
            t('surchargesMaintenance.form.surcharge3Nom')
          ),
          surcharge3Perc: minNumberSchema(
            t('surchargesMaintenance.form.surcharge3Perc')
          ).max(100, { field: t('surchargesMaintenance.form.surcharge3Perc') }),
          valid: yup.object({
            from: yup
              .date()
              .required(t('validationFieldRequired', { name: t('validFrom') }))
              .typeError(t('validationDateInvalid', { name: t('validFrom') }))
              .nullable()
              .default(null),
            to: yup
              .date()
              .required(t('validationFieldRequired', { name: t('validTo') }))
              .typeError(t('validationDateInvalid', { name: t('validTo') }))
              .nullable()
              .default(null)
              .min(
                yup.ref<Date>('from'),
                t('functions.fields.errors.validTo', {
                  from: t('validFrom'),
                  to: t('validTo')
                })
              )
          })
        })
      )
      .required();
  })
});

export const base = yup.object({
  code: minNumberSchema(t('surchargesMaintenance.grid.code'))
    .max(
      9000000000000000,
      t('validation.lessThan', { field: t('surchargesMaintenance.grid.code') })
    )
    .required(
      t('validationFieldRequired', {
        name: t('surchargesMaintenance.grid.code')
      })
    )
    .typeError(
      t('validation.invalid', { name: t('surchargesMaintenance.grid.code') })
    ),
  name: yup.string()
});

export const details = yup.object({
  flags: yup.array().default([]),
  method: yup.string().required(
    t('validationFieldRequired', {
      name: t('surchargesMaintenance.form.method')
    })
  ),
  surcharge1Duty: minNumberSchema(
    t('surchargesMaintenance.form.surcharge1Duty')
  ),
  surcharge1Nom: minNumberSchema(t('surchargesMaintenance.form.surcharge1Nom')),
  surcharge1Perc: minNumberSchema(
    t('surchargesMaintenance.form.surcharge1Perc')
  )
    .max(
      100,
      t('validation.lessThan', {
        field: t('surchargesMaintenance.form.surcharge1Perc')
      })
    )
    .default(0),
  surcharge3Duty: minNumberSchema(
    t('surchargesMaintenance.form.surcharge3Duty')
  ),
  surcharge3Nom: minNumberSchema(t('surchargesMaintenance.form.surcharge3Nom')),
  surcharge3Perc: minNumberSchema(
    t('surchargesMaintenance.form.surcharge3Perc')
  ).max(100, { field: t('surchargesMaintenance.form.surcharge3Perc') }),
  valid: yup.object({
    from: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validFrom') }))
      .nullable()
      .default(null),
    to: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validTo') }))
      .nullable()
      .default(null)
      .min(
        yup.ref<Date>('from'),
        t('functions.fields.errors.validTo', {
          from: t('validFrom'),
          to: t('validTo')
        })
      )
  })
});

export const baseWithDetailsSchema = base.concat(details);

export const editDetailsSchema = base.concat(detailsSchema);
